'use client';

/**
 * /models/[...id] — single AI model detail page.
 *
 * Catch-all route (`[...id]`) so slugs like `openai/gpt-4o` match — the slash
 * is part of the model id, not a directory boundary. Next.js gives us
 * `params.id` as `string[]` which we re-join.
 *
 * Layout:
 *   - Header: brand color stripe, model name, brand, category, slug
 *   - Stats: # prompts using it (live from API)
 *   - Body: PromptGrid filtered to this model via `?model={slug}`
 *
 * The page reads from `useAIModels()` so the registry stays the single source
 * of truth — admin edits flow here without redeploys.
 */
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { api } from '@/lib/api';
import PromptGrid from '@/components/prompt/PromptGrid';
import InfiniteScrollSentinel from '@/components/common/InfiniteScrollSentinel';
import type { Prompt } from '@/types';
import type { AIModel } from '@/lib/ai-models';
import { useAIModels } from '@/lib/ai-models-context';
import { useT } from '@/lib/i18n';


const CATEGORY_LABEL: Record<string, string> = {
  text:  'Text & Chat',
  image: 'Image',
  video: 'Video',
  voice: 'Voice & Audio',
  code:  'Code & Agents',
};


export default function ModelDetailPage() {
  // `[...id]` returns params.id as `string[]`. We rejoin with `/` to get
  // the canonical slug, e.g. `openai/gpt-4o`.
  const params = useParams<{ id: string | string[] }>();
  const router = useRouter();
  const t = useT();
  const slug = Array.isArray(params.id) ? params.id.join('/') : (params.id ?? '');

  const { models, ready: modelsReady } = useAIModels();
  const model: AIModel | undefined = useMemo(
    () => models.find((m) => m.id === slug),
    [models, slug],
  );

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<'newest' | 'top_rated' | 'trending'>('top_rated');
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const SIZE = 20;

  useEffect(() => {
    setLoading(true);
    setPage(1);
    api.prompts.list({ model: slug, sort, page: 1, size: SIZE })
      .then((data) => {
        setPrompts(data.items);
        setTotal(data.total);
      })
      .catch(() => { /* ignore */ })
      .finally(() => setLoading(false));
  }, [slug, sort]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const next = page + 1;
    setPage(next);
    try {
      const data = await api.prompts.list({ model: slug, sort, page: next, size: SIZE });
      setPrompts((prev) => [...prev, ...data.items]);
    } catch {
      /* ignore — sentinel re-arms automatically */
    } finally {
      setLoadingMore(false);
    }
  };

  // Model not found: only conclude that AFTER the registry has loaded
  // (otherwise we'd flash a 404 while the live fetch is in flight).
  if (modelsReady && !model) {
    return (
      <div className="page-pad" style={{ padding: '80px 0', textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
        <h1 style={{
          fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 400,
          margin: '0 0 12px', color: 'var(--ink)',
        }}>
          {t('model_detail.not_found_title')}
        </h1>
        <p style={{ color: 'var(--ink-2)', marginBottom: 28 }}>
          {t('model_detail.not_found_body_pre')} <code style={{ fontFamily: 'var(--font-mono)' }}>{slug}</code> {t('model_detail.not_found_body_post')}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Link href="/models" className="ps-btn ps-btn-primary no-underline">
            <span className="material-symbols-outlined ps-icon">arrow_back</span>
            {t('model_detail.back_to_models')}
          </Link>
          <Link href="/" className="ps-btn ps-btn-secondary no-underline">
            {t('model_detail.browse_prompts')}
          </Link>
        </div>
      </div>
    );
  }

  // Loading shell while models are still fetching.
  if (!model) {
    return (
      <div className="page-pad" style={{ padding: '60px 0' }}>
        <div className="ps-empty"><h3>{t('model_detail.loading')}</h3></div>
      </div>
    );
  }

  const hasMore = prompts.length < total;

  return (
    <>
      {/* Hero */}
      <section className="page-pad" style={{ paddingTop: 32, paddingBottom: 24, maxWidth: 1200 }}>
        <div style={{ marginBottom: 16, fontSize: 12 }}>
          <button
            onClick={() => router.push('/models')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: 'var(--ink-3)',
              background: 'transparent', border: 0, cursor: 'pointer',
              fontSize: 'inherit', font: 'inherit',
              padding: 0,
            }}
            className="hover:text-on-surface"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span>
            {t('model_detail.all_models')}
          </button>
        </div>

        {/* Model header card */}
        <div style={{
          position: 'relative',
          padding: '28px 28px 24px',
          background: 'var(--bg-raised)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}>
          {/* Top brand-color stripe */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 4,
            background: model.color,
          }} />
          {/* Soft tint blob in the corner */}
          <div style={{
            position: 'absolute', top: -40, right: -40,
            width: 220, height: 220, pointerEvents: 'none',
            background: `radial-gradient(circle, ${model.color}33, transparent 65%)`,
          }} />

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 'var(--radius-md)',
              display: 'grid', placeItems: 'center',
              background: `${model.color}1c`,
              color: model.color,
              fontFamily: 'var(--font-serif)', fontWeight: 600, fontSize: 22,
              flexShrink: 0,
            }}>
              {model.icon ? (
                <span className="material-symbols-outlined" style={{ fontSize: 28 }}>{model.icon}</span>
              ) : (
                model.brand[0]
              )}
            </div>

            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.12em', color: model.color,
                marginBottom: 2,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: model.color }} />
                {CATEGORY_LABEL[model.category] ?? model.category}
              </div>
              <h1 style={{
                fontFamily: 'var(--font-serif)',
                fontVariationSettings: '"opsz" 110, "SOFT" 50',
                fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 400,
                letterSpacing: '-0.02em',
                margin: '4px 0 6px',
                color: 'var(--ink)', lineHeight: 1.05,
              }}>
                {model.name}
              </h1>
              <div style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                {t('model_detail.by_brand')} <b style={{ color: 'var(--ink)' }}>{model.brand}</b>
              </div>
              <div style={{
                fontSize: 11, color: 'var(--ink-3)',
                fontFamily: 'var(--font-mono)', marginTop: 6,
              }}>
                {model.id}
              </div>
            </div>

            <div style={{
              display: 'flex', gap: 18,
              padding: '4px 6px',
              alignItems: 'center',
              borderLeft: '1px solid var(--line)',
              paddingLeft: 22,
            }}>
              <Stat label={t('model_detail.stat_prompts')} value={total} hint={t('model_detail.stat_hint')} />
            </div>
          </div>
        </div>
      </section>

      {/* Filter / sort row */}
      <div className="section-hd">
        <div>
          <div className="eyebrow">{t('model_detail.eyebrow')}</div>
          <h2>{t('model_detail.designed_for', { name: model.name })}</h2>
        </div>
        <div className="seg" role="tablist" aria-label="Sort">
          {(['top_rated', 'newest', 'trending'] as const).map((s) => (
            <button
              key={s}
              aria-pressed={sort === s}
              onClick={() => setSort(s)}
            >
              {s === 'top_rated' ? t('model_detail.sort_featured') : s === 'newest' ? t('model_detail.sort_newest') : t('model_detail.sort_top')}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading || prompts.length > 0 ? (
        <div className="page-pad" style={{ paddingBottom: 80 }}>
          <PromptGrid
            prompts={prompts}
            loading={loading}
            emptyMessage={t('model_detail.empty_message', { name: model.name })}
          />
          {!loading && (
            <InfiniteScrollSentinel
              hasMore={hasMore}
              loading={loadingMore}
              onLoadMore={loadMore}
            />
          )}
        </div>
      ) : (
        <div className="ps-empty">
          <h3>{t('model_detail.no_prompts_title', { name: model.name })}</h3>
          <p>{t('model_detail.no_prompts_body')}</p>
          <Link href="/create" className="ps-btn ps-btn-primary no-underline" style={{ marginTop: 14 }}>
            <span className="material-symbols-outlined ps-icon">add</span>
            {t('model_detail.share_a_prompt')}
          </Link>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div>
      <div style={{
        fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 400,
        letterSpacing: '-0.02em', color: 'var(--ink)',
        fontVariantNumeric: 'tabular-nums', lineHeight: 1.05,
      }}>
        {value.toLocaleString()}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2,
        textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 500,
      }}>
        {label}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
